/**
 * voice-v8 — Die eine Wahrheit über die Spieler-Adresse (2026-08-29).
 *
 * ## Warum diese Datei entstanden ist
 *
 * Martin, mit Foto des Spielers: *„Hier fehlt noch der full screen."*
 *
 * Ich hatte `fs=1` gesetzt und den Server neu gestartet — und trotzdem
 * fehlte es. Der Grund ist nicht der Parameter, sondern WO er entsteht:
 * die Adresse baute das Werkzeug auf dem Server und legte sie ALS TEXT in
 * die Chat-Nachricht. Martins Karte war vor dem Neustart entstanden und
 * trug deshalb weiterhin `fs=0` — für immer, denn eine gespeicherte
 * Nachricht ändert sich nicht mehr.
 *
 * ⚠️ Das ist die eigentliche Lehre, nicht das Vollbild: **eine Einstellung,
 * die in eine gespeicherte Nachricht eingebacken wird, lässt sich später
 * nicht mehr korrigieren.** Jede künftige Änderung an den
 * Spieler-Parametern hätte dasselbe Problem gehabt.
 *
 * Deshalb ist die Adresse jetzt keine gespeicherte Tatsache mehr, sondern
 * wird beim Abspielen neu gebildet. Gespeichert bleibt nur die
 * Video-Kennung — die ändert sich nie.
 *
 * Beide Seiten lesen dieselben Parameter: der Server beim Erzeugen
 * (`tools/fokus_videos.ts`), die Oberfläche beim Abspielen
 * (`frontend/lib/tool-cards.ts`). Zwei Listen wären eine zu viel.
 */

/**
 * Die Parameter des eingebetteten Spielers — mit dem Grund für jeden.
 *
 * ⚠️ `modestbranding` steht bewusst NICHT hier. Seit 15.08.2023 sagt die
 * Doku wörtlich: „This parameter is deprecated and has no effect." Es steht
 * noch in `tools/play_music.ts` und wirkt dort seit Jahren nicht — ein
 * Parameter, den man für einen Riegel hält, ist schlimmer als keiner.
 */
export const SPIELER_PARAMETER: Readonly<Record<string, string>> = {
  /*
   * Schaltet KEINE Empfehlungen ab. Seit 25.09.2018: „you will not be able
   * to disable related videos. Instead, if the rel parameter is set to 0,
   * related videos will come from the same channel." Bleibt drin, weil es
   * gratis ist — aber wer glaubt, es schließe den Endbildschirm, irrt.
   */
  rel: "0",
  /*
   * ⚠️ Vollbild ERLAUBT. Ich hatte hier `0` — kein Vollbild, weil die
   * Spielerdekoration dort am größten ist. Martin, nachdem er es benutzt
   * hat: „Geht, nur vergrößerbar ist es nicht."
   *
   * Mein Grund war schwach: auf 390 Punkten bleibt ein Bild von 356x200
   * Punkten. Bei einem Handgriff an der Wand ist das zu klein, um zu SEHEN,
   * was gezeigt wird — und Sehen ist der einzige Grund, warum dort
   * überhaupt ein Video steht statt einer gesprochenen Antwort. Eine
   * Abschirmung, die den Zweck kostet, ist keine.
   */
  fs: "1",
  /** Kein versehentliches Wegspringen per Tastatur. */
  disablekb: "1",
  /** iOS: kein Sprung in den nativen Vollbildspieler beim Start. */
  playsinline: "1",
  /*
   * Betrifft die alten Annotations, die YouTube im Januar 2019 selbst
   * abgeschafft hat. ⚠️ Hilft NICHT gegen die End Screens des Uploaders —
   * das ist eine andere Sache und nicht abschaltbar.
   */
  iv_load_policy: "3",
};

/**
 * ⚠️ `youtube-nocookie.com`, NICHT `youtube.com` — und zwar aus einem Grund,
 * der bei der Sucht liegt, nicht beim Datenschutz. YouTube sagt zu diesem
 * Modus, die Wiedergabe werde nicht benutzt, um „the viewer's subsequent
 * YouTube viewing experience" zu personalisieren. Heißt: sieht Martin drei
 * Wochen lang über Vespera Videos zu einem schweren Thema, wartet auf
 * youtube.com KEINE passende Startseite auf ihn. Der Köder für später wird
 * gar nicht erst gelegt. Das ist der wirksamste Sucht-Schutz des Moduls —
 * wirksamer als jeder Parameter oben.
 */
const GRUND = "https://www.youtube-nocookie.com/embed/";

/** Eine YouTube-Kennung ist elf Zeichen aus Buchstaben, Ziffern, `-` und `_`. */
const KENNUNG = /^[\w-]{11}$/;

/**
 * Die Video-Kennung aus einer Kennung ODER einer gespeicherten Adresse.
 *
 * Gibt `null` zurück, wenn nichts Brauchbares drinsteht — der Aufrufer
 * entscheidet dann, ob er gar nichts anzeigt. Raten wäre schlimmer.
 */
export function videoKennung(was: string): string | null {
  const s = (was ?? "").trim();
  if (KENNUNG.test(s)) return s;
  const m = /\/embed\/([\w-]{11})/.exec(s);
  return m ? m[1]! : null;
}

/**
 * Die Adresse für den eingebetteten Spieler.
 *
 * Nimmt eine Kennung oder eine ältere gespeicherte Adresse und baut sie
 * FRISCH auf — dadurch bekommt auch eine Karte von gestern die heutigen
 * Parameter.
 */
export function spielerAdresse(was: string): string | null {
  const id = videoKennung(was);
  if (!id) return null;
  return `${GRUND}${id}?${new URLSearchParams(SPIELER_PARAMETER)}`;
}
